import { useQuery } from 'urql';
import { useMemo } from 'react';
import { getMetaAdSetsByCampaign } from '../graphql';
import type { AdSetsByCampaignData } from './types';

// Helper function to check if a value is defined
const isDefined = <T>(value: T | null | undefined): value is T => {
  return value !== null && value !== undefined;
};

export const useAdSetsByCampaign = ({
  clientId,
  campaignIds,
  pause = false,
}: {
  clientId: string;
  campaignIds: string[];
  pause?: boolean;
}) => {
  const [{ data, fetching, error }] = useQuery<AdSetsByCampaignData>({
    query: getMetaAdSetsByCampaign,
    variables: { clientId, campaignIds },
    pause: pause || !clientId || campaignIds.length === 0,
  });

  // Memoized flattening of ad sets from all campaigns across all accounts
  const allAdSets = useMemo(() => {
    return (
      data?.client?.metaAdAccounts?.flatMap(
        (account) =>
          account?.metaAdCampaigns?.flatMap(
            (campaign) =>
              campaign?.metaAdSets?.map((adSet) => ({
                ...adSet,
                campaignId: campaign.id,
                campaignName: campaign.name,
              })) || [],
          ) || [],
      ) || []
    ).filter(isDefined);
  }, [data?.client?.metaAdAccounts]);

  // Memoized campaigns with ad sets
  const campaignsWithAdSets = useMemo(() => {
    return data?.client?.metaAdAccounts?.flatMap((account) => account?.metaAdCampaigns?.filter(isDefined) || []) || [];
  }, [data?.client?.metaAdAccounts]);

  return {
    data,
    fetching,
    error,
    adSets: allAdSets.filter((adSet) => adSet.id),
    campaignsWithAdSets,
  };
};
